import { Component, ElementRef, EventEmitter, Input, Output, ViewChild, AfterViewInit, OnDestroy } from '@angular/core';

declare const Dropzone: any;

@Component({
  selector: 'app-fileorini',
  standalone: false,
  templateUrl: './fileorini.html',
  styleUrl: './fileorini.css',
})
export class Fileorini implements AfterViewInit, OnDestroy {
  @ViewChild('dzRef', { static: false }) dzRef?: ElementRef<HTMLDivElement>;

  /** Accept mime string like 'image/*,.pdf' */
  @Input() accept = 'image/*';
  /** Optional max file size in MB */
  @Input() maxSizeMB: number | null = null;
  /** 1 = single file, >1 = multi */
  @Input() maxFiles = 1;

  /** Custom placeholder HTML (Dropzone message) */
  @Input() placeholderHtml = `
    <div class="dz-message needsclick">
      <i class="ki-duotone ki-file-up fs-3x text-primary">
        <span class="path1"></span><span class="path2"></span>
      </i>
      <div class="ms-4 d-flex align-items-center">
        <h3 class="fs-5 fw-bold text-gray-900 m-0">
          Drop file here or click to upload.
        </h3>
      </div>
    </div>
  `;

  /** Emits the current files array (empty array when none) */
  @Output() filesChanged = new EventEmitter<File[]>();

  private dz: any;
  private emitScheduled = false;

  ngAfterViewInit(): void {
    // wait one microtask so the view is fully stamped out
    Promise.resolve().then(() => this.initDropzone());
  }

  ngOnDestroy(): void {
    // Clean up Dropzone instance
    try { this.dz?.destroy?.(); } catch {}
  }

  private scheduleEmit() {
    if (this.emitScheduled) return;
    this.emitScheduled = true;

    // microtask: let Dropzone finish its add/remove cycle
    Promise.resolve().then(() => {
      this.emitScheduled = false;
      const files: File[] = (this.dz?.files || [])
        .filter((f: any) => f.accepted && f.status !== 'canceled')
        .map((f: any) => f as File);
      this.filesChanged.emit(files);
    });
  }

  private initDropzone(attempt = 0): void {
    const el = this.dzRef?.nativeElement;
    if (!el) {
      if (attempt < 10) {
        requestAnimationFrame(() => this.initDropzone(attempt + 1));
      } else {
        console.warn('Dropzone element not found for #dzRef');
      }
      return;
    }

    if (typeof Dropzone !== 'undefined') Dropzone.autoDiscover = false;

    this.dz = new Dropzone(el, {
      url: '#', // picker only (no upload)
      autoProcessQueue: false,
      maxFiles: this.maxFiles,
      uploadMultiple: false,
      parallelUploads: 1,
      addRemoveLinks: true,
      acceptedFiles: this.accept,
      dictDefaultMessage: this.placeholderHtml,
      maxFilesize: this.maxSizeMB ?? undefined,
      previewTemplate: `
        <div class="dz-preview dz-file-preview">
          <div class="dz-image"><img data-dz-thumbnail /></div>
          <a class="dz-remove" data-dz-remove>&times;</a>
        </div>
      `,
      init: function () {
        // If maxFiles is 1, replace the existing file when a new one is added
        this.on('maxfilesexceeded', (file: File) => {
          this.removeAllFiles();
          this.addFile(file);
        });
      },
    });

    // Coalesce emissions so first file isn’t “missed”
    this.dz.on('addedfile', () => this.scheduleEmit());
    this.dz.on('removedfile', () => this.scheduleEmit());

    // Do not actually upload anything
    this.dz.on('sending', (_file: File, xhr: any) => {
      if (this.dz.options.autoProcessQueue === false) xhr?.abort?.();
    });
  }
}


